import type { DropResult } from "@hello-pangea/dnd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  type ApiError,
  type QuestionPublic,
  QuestionsService,
  type QuizPublic,
  type QuizSectionPublic,
  QuizzesService,
} from "@/client"
import type {
  ProgramsPublic,
  QuizCreate,
  QuizTemplateSettings,
} from "@/client/types.gen"
import type { ProgramOption } from "@/components/Programs/ProgramSelector"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

import type {
  QuizBuilderMode,
  QuizSection,
  SelectedQuestion,
} from "../quiz-builder-types"

type QuizSettings = QuizCreate & {
  publish_date: string | null
}

type QuizTemplateSettingsForBuilder = QuizTemplateSettings

type QuizPublicWithPublishDate = QuizPublic & {
  publish_date?: string | null
}

type QuizRequestPayload = QuizCreate & {
  publish_date?: string | null
}

const DEFAULT_SETTINGS: QuizSettings = {
  name: "",
  publish_date: null,
  cutoff_date: null,
  time_limit_minutes: null,
  allow_retake: false,
  show_result_mode: "immediate",
  result_date: null,
  show_percentile: false,
  show_score_comparison: false,
  show_rationale_mode: "after_all",
  show_peer_performance: false,
  allow_student_timer: true,
  allow_mpl_transmutation: false,
  mpl_per_subject: false,
  allow_search_without_attempt: false,
  allow_rationale_without_attempt: false,
  tags: [],
}

type QuizInitializationPayload = {
  quiz: QuizPublicWithPublishDate
  questions: SelectedQuestion[]
  sections: QuizSectionPublic[]
}

interface QuizBuilderContextValue {
  // State
  settings: QuizSettings
  selectedPrograms: ProgramOption[]
  searchValue: string
  debouncedSearch: string
  selectedSubjects: Array<{ id: string; name: string }>
  selectedTopics: Array<{ id: string; name: string }>
  sections: QuizSection[]
  unsectionedQuestions: SelectedQuestion[]
  activeTab: "builder" | "preview" | "settings"
  isBatchUploadOpen: boolean

  // Computed
  allSelectedQuestions: SelectedQuestion[]
  availableQuestions: QuestionPublic[]
  isQuestionSelected: (questionId: string) => boolean
  isSaving: boolean
  hasUnsavedChanges: boolean

  // Data
  loadingQuestions: boolean
  quizInitializationData: QuizInitializationPayload | undefined

  // Actions
  setSearchValue: (value: string) => void
  setSelectedSubjects: (subjects: Array<{ id: string; name: string }>) => void
  setSelectedTopics: (topics: Array<{ id: string; name: string }>) => void
  setSelectedPrograms: (programs: ProgramOption[]) => void
  setActiveTab: (tab: "builder" | "preview" | "settings") => void
  setIsBatchUploadOpen: (open: boolean) => void

  handleAddQuestion: (question: QuestionPublic) => void
  handleRemoveQuestion: (questionId: string, sectionId?: string) => void
  handleClearSectionQuestions: (sectionId: string) => void
  handleAddSection: () => void
  handleRemoveSection: (sectionId: string) => void
  handleUpdateSection: (sectionId: string, updates: { name?: string }) => void
  handleToggleSection: (sectionId: string) => void
  handleDragEnd: (result: DropResult) => void
  handleSettingChange: <K extends keyof QuizSettings>(
    key: K,
    value: QuizSettings[K],
  ) => void
  buildTemplateSettingsPayload: () => QuizTemplateSettingsForBuilder
  applyTemplateSettings: (
    templateSettings: QuizTemplateSettingsForBuilder,
  ) => void
  handleSaveQuiz: () => void
  handleBatchUploadSuccess: () => Promise<void>
}

const QuizBuilderContext = createContext<QuizBuilderContextValue | undefined>(
  undefined,
)

interface QuizBuilderProviderProps {
  mode: QuizBuilderMode
  quizId?: string
  children: ReactNode
}

function normalizeDateForInput(value?: string | null): string | null {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  const offset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

function buildSectionSnapshot(
  sections: QuizSection[],
  unsectionedQuestions: SelectedQuestion[],
) {
  return {
    sections: sections.map((section) => ({
      id: section.id,
      name: section.name,
      description: section.description ?? null,
      questionIds: section.questions.map((question) => question.id),
    })),
    unsectionedQuestionIds: unsectionedQuestions.map((question) => question.id),
  }
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value)
}

export function QuizBuilderProvider({
  mode,
  quizId,
  children,
}: QuizBuilderProviderProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const [settings, setSettings] = useState<QuizSettings>({
    ...DEFAULT_SETTINGS,
  })
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([])
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [sections, setSections] = useState<QuizSection[]>([])
  const [unsectionedQuestions, setUnsectionedQuestions] = useState<
    SelectedQuestion[]
  >([])
  const [activeTab, setActiveTab] = useState<
    "builder" | "preview" | "settings"
  >("settings")
  const [isBatchUploadOpen, setIsBatchUploadOpen] = useState(false)

  const hasInitializedEditState = useRef(false)

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue.trim())
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  // Fetch questions for question bank
  const { data: questionsData, isLoading: loadingQuestions } = useQuery({
    queryKey: [
      "quiz-builder-questions",
      {
        search: debouncedSearch || undefined,
        subjectIds: selectedSubjects.map((s) => s.id),
        topicIds: selectedTopics.map((t) => t.id),
      },
    ],
    queryFn: () =>
      QuestionsService.listQuestions({
        limit: 100,
        search: debouncedSearch || undefined,
        subjectIds:
          selectedSubjects.length > 0
            ? selectedSubjects.map((s) => s.id)
            : undefined,
        topicIds:
          selectedTopics.length > 0
            ? selectedTopics.map((t) => t.id)
            : undefined,
      }),
  })

  // Fetch quiz data for edit mode
  const { data: quizInitializationData, error: quizInitializationError } =
    useQuery<
      QuizInitializationPayload,
      ApiError,
      QuizInitializationPayload,
      ["quiz-builder", string | undefined]
    >({
      enabled: mode === "edit" && Boolean(quizId),
      queryKey: ["quiz-builder", quizId],
      queryFn: async (): Promise<QuizInitializationPayload> => {
        if (!quizId) {
          throw new Error("Quiz ID is required for edit mode")
        }

        const [quiz, sectionsResponse] = await Promise.all([
          QuizzesService.getQuiz({ quizId }),
          QuizzesService.listQuizSections({ quizId }),
        ])
        const orderedQuestions = [...(quiz.questions ?? [])].sort(
          (a, b) => a.order - b.order,
        )

        const questions: SelectedQuestion[] = orderedQuestions.map(
          (questionWithData) => {
            const questionData: QuestionPublic = {
              id: questionWithData.question_id,
              question_text: questionWithData.question_text,
              options: questionWithData.options,
              correct_answer: questionWithData.correct_answer as number,
              rationale: questionWithData.rationale ?? null,
              allow_practice_quiz: false,
              org_id: quiz.org_id,
              created_by: quiz.created_by,
              created_at: new Date().toISOString(),
              subjects: [],
              topics: [],
            }

            return {
              id: questionWithData.question_id,
              data: questionData,
            }
          },
        )

        return { quiz, questions, sections: sectionsResponse ?? [] }
      },
    })

  // Fetch programs for edit mode
  const { data: quizProgramsData } = useQuery<ProgramsPublic>({
    enabled: mode === "edit" && Boolean(quizId),
    queryKey: ["quiz-programs", quizId],
    queryFn: async () => {
      if (!quizId) {
        throw new Error("Quiz ID is required for loading programs")
      }
      const response = await QuizzesService.listQuizPrograms({ quizId })
      return response as ProgramsPublic
    },
  })

  // Handle quiz initialization error
  useEffect(() => {
    if (quizInitializationError) {
      handleError(quizInitializationError as ApiError)
    }
  }, [quizInitializationError])

  // Initialize programs from quiz data
  useEffect(() => {
    if (mode === "create") {
      setSelectedPrograms([])
      return
    }
    if (mode === "edit" && quizProgramsData) {
      setSelectedPrograms(
        quizProgramsData.data.map((program) => ({
          id: program.id,
          name: program.name,
          status: program.status,
        })),
      )
    }
  }, [mode, quizProgramsData])

  // Initialize quiz data for edit mode
  useEffect(() => {
    if (
      mode !== "edit" ||
      !quizInitializationData ||
      hasInitializedEditState.current
    ) {
      return
    }

    const {
      quiz,
      questions,
      sections: existingSections,
    } = quizInitializationData
    console.debug("[QuizBuilder] initializing from quiz data", {
      quizId: quiz.id,
      questionOrder: questions.map((item) => item.id),
    })
    setSettings({
      name: quiz.name,
      publish_date: normalizeDateForInput(quiz.publish_date),
      cutoff_date: normalizeDateForInput(quiz.cutoff_date),
      time_limit_minutes:
        typeof quiz.time_limit_minutes === "number"
          ? quiz.time_limit_minutes
          : null,
      allow_retake: quiz.allow_retake ?? false,
      show_result_mode: quiz.show_result_mode ?? "immediate",
      result_date: normalizeDateForInput(quiz.result_date),
      show_percentile: quiz.show_percentile ?? false,
      show_score_comparison: quiz.show_score_comparison ?? false,
      show_rationale_mode: quiz.show_rationale_mode ?? "after_all",
      show_peer_performance: quiz.show_peer_performance ?? false,
      allow_student_timer: quiz.allow_student_timer ?? true,
      allow_mpl_transmutation: quiz.allow_mpl_transmutation ?? false,
      mpl_per_subject: quiz.mpl_per_subject ?? false,
      allow_search_without_attempt: quiz.allow_search_without_attempt ?? false,
      allow_rationale_without_attempt:
        quiz.allow_rationale_without_attempt ?? false,
      tags: quiz.tags ?? [],
    })

    const orderedLinks = [...(quiz.questions ?? [])].sort(
      (a, b) => a.order - b.order,
    )

    const sectionMap = new Map<string, QuizSection>()
    const sectionsToHydrate = (existingSections ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)

    for (const section of sectionsToHydrate) {
      sectionMap.set(section.id, {
        id: section.id,
        name: section.name,
        description: section.description ?? null,
        order: section.order,
        questions: [],
        isCollapsed: false,
      })
    }

    const unsectioned: SelectedQuestion[] = []

    for (let index = 0; index < orderedLinks.length; index += 1) {
      const link = orderedLinks[index]
      const question = questions[index]
      if (!question) {
        continue
      }

      if (link.section_id) {
        const targetSection = sectionMap.get(link.section_id)
        if (targetSection) {
          targetSection.questions.push(question)
          continue
        }
      }

      unsectioned.push(question)
    }

    setSections(Array.from(sectionMap.values()))
    setUnsectionedQuestions(unsectioned)
    hasInitializedEditState.current = true
  }, [mode, quizInitializationData])

  // Both of these settings are inert once rationales are hidden entirely, and
  // their checkboxes are disabled in that state. Reset them rather than leaving
  // a true value parked out of sight: it would silently reactivate the moment
  // the teacher switched the rationale mode back on.
  useEffect(() => {
    if (settings.show_rationale_mode !== "never") {
      return
    }
    if (
      !settings.show_peer_performance &&
      !settings.allow_rationale_without_attempt
    ) {
      return
    }
    setSettings((prev) => ({
      ...prev,
      show_peer_performance: false,
      allow_rationale_without_attempt: false,
    }))
  }, [
    settings.show_rationale_mode,
    settings.show_peer_performance,
    settings.allow_rationale_without_attempt,
  ])

  useEffect(() => {
    if (settings.show_percentile && !settings.show_score_comparison) {
      setSettings((prev) => ({
        ...prev,
        show_score_comparison: true,
      }))
    }
  }, [settings.show_percentile, settings.show_score_comparison])

  // Computed values
  const programIds = useMemo(
    () => selectedPrograms.map((program) => program.id),
    [selectedPrograms],
  )

  const allSelectedQuestions = useMemo(() => {
    const questionsFromSections = sections.flatMap(
      (section) => section.questions,
    )
    return [...questionsFromSections, ...unsectionedQuestions]
  }, [sections, unsectionedQuestions])

  const availableQuestions = useMemo(
    () => questionsData?.data ?? [],
    [questionsData?.data],
  )

  const isQuestionSelected = useCallback(
    (questionId: string) => {
      return allSelectedQuestions.some((item) => item.id === questionId)
    },
    [allSelectedQuestions],
  )

  // Single mutation that reconciles the entire quiz state in one round trip.
  const saveQuizMutation = useMutation({
    mutationFn: async (payload: {
      quizId: string | null
      settings: QuizCreate
      programIds: string[]
      sections: QuizSection[]
      unsectionedQuestionIds: string[]
    }) => {
      const sectionPayload = payload.sections.map((section) => ({
        id: section.id.startsWith("temp-") ? null : section.id,
        name: section.name,
        description: section.description ?? null,
        question_ids: section.questions.map((q) => q.id),
      }))

      const quiz = await QuizzesService.saveQuiz({
        requestBody: {
          quiz_id: payload.quizId,
          settings: payload.settings,
          program_ids: payload.programIds,
          sections: sectionPayload,
          unsectioned_question_ids: payload.unsectionedQuestionIds,
        },
      })
      return { quiz, wasCreate: payload.quizId === null }
    },
    onSuccess: async ({ quiz, wasCreate }) => {
      queryClient.invalidateQueries({ queryKey: ["quizzes"] })
      queryClient.invalidateQueries({ queryKey: ["quiz-builder", quiz.id] })
      queryClient.invalidateQueries({ queryKey: ["quiz-programs", quiz.id] })
      queryClient.invalidateQueries({ queryKey: ["program-quizzes"] })
      showSuccessToast(
        wasCreate ? "Quiz saved successfully" : "Quiz updated successfully",
      )
      if (wasCreate) {
        navigate({ to: "/quiz-management" })
      }
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  // Event handlers
  const handleAddQuestion = useCallback((question: QuestionPublic) => {
    setUnsectionedQuestions((prev) => {
      if (prev.some((item) => item.id === question.id)) {
        console.debug("[QuizBuilder] attempt to add existing question", {
          questionId: question.id,
        })
        return prev
      }

      console.debug("[QuizBuilder] adding question to unsectioned", {
        questionId: question.id,
      })
      return [...prev, { id: question.id, data: question }]
    })
  }, [])

  const handleRemoveQuestion = useCallback(
    (questionId: string, sectionId?: string) => {
      console.debug("[QuizBuilder] removing question", {
        questionId,
        sectionId,
      })

      if (sectionId && sectionId !== "unsectioned") {
        setSections((prev) =>
          prev.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  questions: section.questions.filter(
                    (q) => q.id !== questionId,
                  ),
                }
              : section,
          ),
        )
      } else {
        setUnsectionedQuestions((prev) =>
          prev.filter((item) => item.id !== questionId),
        )
      }
    },
    [],
  )

  const handleClearSectionQuestions = useCallback((sectionId: string) => {
    if (sectionId === "unsectioned") {
      setUnsectionedQuestions([])
      return
    }
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, questions: [] } : section,
      ),
    )
  }, [])

  const handleAddSection = useCallback(() => {
    const newSection: QuizSection = {
      id: `temp-${Date.now()}`,
      name: `Section ${sections.length + 1}`,
      order: sections.length + 1,
      questions: [],
      isCollapsed: false,
    }
    setSections((prev) => [...prev, newSection])
  }, [sections.length])

  const handleRemoveSection = useCallback((sectionId: string) => {
    setSections((prev) => {
      const section = prev.find((s) => s.id === sectionId)
      if (section && section.questions.length > 0) {
        setUnsectionedQuestions((unsectioned) => [
          ...unsectioned,
          ...section.questions,
        ])
      }
      return prev.filter((s) => s.id !== sectionId)
    })
  }, [])

  const handleUpdateSection = useCallback(
    (sectionId: string, updates: { name?: string }) => {
      if (sectionId === "unsectioned") {
        const newSection: QuizSection = {
          id: `temp-${Date.now()}`,
          name: updates.name || "Unsectioned",
          order: sections.length + 1,
          questions: unsectionedQuestions,
          isCollapsed: false,
        }
        setSections((prev) => [...prev, newSection])
        setUnsectionedQuestions([])
        return
      }

      setSections((prev) =>
        prev.map((section) =>
          section.id === sectionId ? { ...section, ...updates } : section,
        ),
      )
    },
    [sections.length, unsectionedQuestions],
  )

  const handleToggleSection = useCallback((sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, isCollapsed: !section.isCollapsed }
          : section,
      ),
    )
  }, [])

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination, type } = result

    if (!destination) {
      return
    }

    if (type === "SECTION") {
      setSections((prev) => {
        const updated = [...prev]
        const [moved] = updated.splice(source.index, 1)
        updated.splice(destination.index, 0, moved)
        return updated.map((section, index) => ({
          ...section,
          order: index + 1,
        }))
      })
      return
    }

    if (type === "QUESTION") {
      const sourceDroppableId = source.droppableId
      const destDroppableId = destination.droppableId

      const sourceSectionId = sourceDroppableId.includes("section-")
        ? sourceDroppableId.replace("section-", "").replace("-questions", "")
        : "unsectioned"

      const destSectionId = destDroppableId.includes("section-")
        ? destDroppableId.replace("section-", "").replace("-questions", "")
        : "unsectioned"

      if (sourceSectionId === destSectionId) {
        if (sourceSectionId === "unsectioned") {
          setUnsectionedQuestions((prev) => {
            const updated = [...prev]
            const [moved] = updated.splice(source.index, 1)
            updated.splice(destination.index, 0, moved)
            return updated
          })
        } else {
          setSections((prev) =>
            prev.map((section) =>
              section.id === sourceSectionId
                ? {
                    ...section,
                    questions: (() => {
                      const updated = [...section.questions]
                      const [moved] = updated.splice(source.index, 1)
                      updated.splice(destination.index, 0, moved)
                      return updated
                    })(),
                  }
                : section,
            ),
          )
        }
      } else {
        let movedQuestion: SelectedQuestion | null = null

        if (sourceSectionId === "unsectioned") {
          setUnsectionedQuestions((prev) => {
            const updated = [...prev]
            ;[movedQuestion] = updated.splice(source.index, 1)
            return updated
          })
        } else {
          setSections((prev) =>
            prev.map((section) => {
              if (section.id === sourceSectionId) {
                const updated = [...section.questions]
                ;[movedQuestion] = updated.splice(source.index, 1)
                return { ...section, questions: updated }
              }
              return section
            }),
          )
        }

        setTimeout(() => {
          if (!movedQuestion) return

          const questionToMove = movedQuestion

          if (destSectionId === "unsectioned") {
            setUnsectionedQuestions((prev) => {
              const updated = [...prev]
              updated.splice(destination.index, 0, questionToMove)
              return updated
            })
          } else {
            setSections((prev) =>
              prev.map((section) => {
                if (section.id === destSectionId) {
                  const updated = [...section.questions]
                  updated.splice(destination.index, 0, questionToMove)
                  return { ...section, questions: updated }
                }
                return section
              }),
            )
          }
        }, 0)
      }
    }
  }, [])

  const handleSettingChange = <K extends keyof QuizSettings>(
    key: K,
    value: QuizSettings[K],
  ) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const toIsoString = (value?: string | null) =>
    value ? new Date(value).toISOString() : null

  const buildRequestPayload = (): QuizRequestPayload => ({
    name: settings.name.trim(),
    publish_date: toIsoString(settings.publish_date),
    cutoff_date: toIsoString(settings.cutoff_date),
    time_limit_minutes:
      settings.time_limit_minutes !== null &&
      settings.time_limit_minutes !== undefined
        ? Number(settings.time_limit_minutes)
        : null,
    allow_retake: settings.allow_retake ?? false,
    show_result_mode: settings.show_result_mode ?? "immediate",
    result_date:
      settings.show_result_mode === "scheduled"
        ? toIsoString(settings.result_date)
        : null,
    show_percentile: settings.show_percentile ?? false,
    show_score_comparison: settings.show_score_comparison ?? false,
    show_rationale_mode: settings.show_rationale_mode ?? "after_all",
    show_peer_performance:
      settings.show_rationale_mode === "never"
        ? false
        : settings.show_peer_performance ?? false,
    allow_mpl_transmutation: settings.allow_mpl_transmutation ?? false,
    allow_student_timer: settings.allow_student_timer ?? true,
    mpl_per_subject: settings.mpl_per_subject ?? false,
    allow_search_without_attempt:
      settings.allow_search_without_attempt ?? false,
    allow_rationale_without_attempt:
      settings.allow_rationale_without_attempt ?? false,
    tags: settings.tags ?? [],
  })

  const buildTemplateSettingsPayload = (): QuizTemplateSettingsForBuilder => ({
    publish_date: null,
    cutoff_date: null,
    time_limit_minutes:
      settings.time_limit_minutes !== null &&
      settings.time_limit_minutes !== undefined
        ? Number(settings.time_limit_minutes)
        : null,
    allow_retake: settings.allow_retake ?? false,
    show_result_mode: settings.show_result_mode ?? "immediate",
    result_date: null,
    show_percentile: settings.show_percentile ?? false,
    show_score_comparison: settings.show_score_comparison ?? false,
    show_rationale_mode: settings.show_rationale_mode ?? "after_all",
    show_peer_performance:
      settings.show_rationale_mode === "never"
        ? false
        : settings.show_peer_performance ?? false,
    allow_search_without_attempt:
      settings.allow_search_without_attempt ?? false,
    allow_rationale_without_attempt:
      settings.show_rationale_mode === "never"
        ? false
        : settings.allow_rationale_without_attempt ?? false,
    allow_mpl_transmutation: settings.allow_mpl_transmutation ?? false,
    allow_student_timer: settings.allow_student_timer ?? true,
    mpl_per_subject: settings.mpl_per_subject ?? false,
    tags: settings.tags ?? [],
  })

  const applyTemplateSettings = (
    templateSettings: QuizTemplateSettingsForBuilder,
  ) => {
    setSettings((prev) =>
      applyTemplateSettingsToCurrentSettings(prev, templateSettings),
    )
  }

  const handleSaveQuiz = () => {
    if (!settings.name.trim()) {
      showErrorToast("Quiz name is required")
      return
    }

    const payload = buildRequestPayload()

    if (mode === "edit" && !quizId) {
      showErrorToast("Missing quiz identifier")
      return
    }

    saveQuizMutation.mutate({
      quizId: mode === "edit" ? (quizId as string) : null,
      settings: payload as QuizCreate,
      programIds,
      sections,
      unsectionedQuestionIds: unsectionedQuestions.map((item) => item.id),
    })
  }

  const handleBatchUploadSuccess = async () => {
    hasInitializedEditState.current = false
    await queryClient.invalidateQueries({ queryKey: ["quiz-builder", quizId] })
    await queryClient.refetchQueries({ queryKey: ["quiz-builder", quizId] })
    setIsBatchUploadOpen(false)
  }

  const isSaving = saveQuizMutation.isPending

  const hasUnsavedChanges = useMemo(() => {
    if (mode !== "edit" || !quizInitializationData || !quizProgramsData) {
      return false
    }

    const savedSettings = {
      name: quizInitializationData.quiz.name,
      publish_date: normalizeDateForInput(
        quizInitializationData.quiz.publish_date,
      ),
      cutoff_date: normalizeDateForInput(
        quizInitializationData.quiz.cutoff_date,
      ),
      time_limit_minutes:
        typeof quizInitializationData.quiz.time_limit_minutes === "number"
          ? quizInitializationData.quiz.time_limit_minutes
          : null,
      allow_retake: quizInitializationData.quiz.allow_retake ?? false,
      show_result_mode:
        quizInitializationData.quiz.show_result_mode ?? "immediate",
      result_date: normalizeDateForInput(
        quizInitializationData.quiz.result_date,
      ),
      show_percentile: quizInitializationData.quiz.show_percentile ?? false,
      show_score_comparison:
        quizInitializationData.quiz.show_score_comparison ?? false,
      show_rationale_mode:
        quizInitializationData.quiz.show_rationale_mode ?? "after_all",
      show_peer_performance:
        quizInitializationData.quiz.show_peer_performance ?? false,
      allow_student_timer:
        quizInitializationData.quiz.allow_student_timer ?? true,
      allow_mpl_transmutation:
        quizInitializationData.quiz.allow_mpl_transmutation ?? false,
      mpl_per_subject: quizInitializationData.quiz.mpl_per_subject ?? false,
      allow_search_without_attempt:
        quizInitializationData.quiz.allow_search_without_attempt ?? false,
      allow_rationale_without_attempt:
        quizInitializationData.quiz.allow_rationale_without_attempt ?? false,
      tags: quizInitializationData.quiz.tags ?? [],
    }

    const savedSections = (quizInitializationData.sections ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((section) => ({
        id: section.id,
        name: section.name,
        description: section.description ?? null,
        questionIds: (quizInitializationData.quiz.questions ?? [])
          .filter((question) => question.section_id === section.id)
          .sort((a, b) => a.order - b.order)
          .map((question) => question.question_id),
      }))

    const savedUnsectionedQuestionIds = (
      quizInitializationData.quiz.questions ?? []
    )
      .filter((question) => !question.section_id)
      .sort((a, b) => a.order - b.order)
      .map((question) => question.question_id)

    const currentSnapshot = {
      settings,
      programIds: programIds.slice().sort(),
      ...buildSectionSnapshot(sections, unsectionedQuestions),
    }

    const savedSnapshot = {
      settings: savedSettings,
      programIds: (quizProgramsData.data ?? [])
        .map((program) => program.id)
        .sort(),
      sections: savedSections,
      unsectionedQuestionIds: savedUnsectionedQuestionIds,
    }

    return stableStringify(currentSnapshot) !== stableStringify(savedSnapshot)
  }, [
    mode,
    programIds,
    quizInitializationData,
    quizProgramsData,
    sections,
    settings,
    unsectionedQuestions,
  ])

  const value: QuizBuilderContextValue = {
    // State
    settings,
    selectedPrograms,
    searchValue,
    debouncedSearch,
    selectedSubjects,
    selectedTopics,
    sections,
    unsectionedQuestions,
    activeTab,
    isBatchUploadOpen,

    // Computed
    allSelectedQuestions,
    availableQuestions,
    isQuestionSelected,
    isSaving,
    hasUnsavedChanges,

    // Data
    loadingQuestions,
    quizInitializationData,

    // Actions
    setSearchValue,
    setSelectedSubjects,
    setSelectedTopics,
    setSelectedPrograms,
    setActiveTab,
    setIsBatchUploadOpen,

    handleAddQuestion,
    handleRemoveQuestion,
    handleClearSectionQuestions,
    handleAddSection,
    handleRemoveSection,
    handleUpdateSection,
    handleToggleSection,
    handleDragEnd,
    handleSettingChange,
    buildTemplateSettingsPayload,
    applyTemplateSettings,
    handleSaveQuiz,
    handleBatchUploadSuccess,
  }

  return (
    <QuizBuilderContext.Provider value={value}>
      {children}
    </QuizBuilderContext.Provider>
  )
}

export function useQuizBuilder() {
  const context = useContext(QuizBuilderContext)
  if (!context) {
    throw new Error("useQuizBuilder must be used within QuizBuilderProvider")
  }
  return context
}

export function applyTemplateSettingsToCurrentSettings(
  currentSettings: QuizSettings,
  templateSettings: QuizTemplateSettingsForBuilder,
): QuizSettings {
  return {
    ...currentSettings,
    ...templateSettings,
    publish_date: currentSettings.publish_date ?? null,
    cutoff_date: currentSettings.cutoff_date ?? null,
    result_date: currentSettings.result_date ?? null,
  }
}

export type { QuizSettings, QuizTemplateSettingsForBuilder }
